import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./leaderboard.css";
import { alreadyPlayed, getLeaderboard, updategameover } from "../../scoreservice";

function Leaderboard({ updateHeader }) {
  const navigate = useNavigate();
  const [themeData, setThemeData] = useState(null);
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGameoverTrue, setIsGameoverTrue] = useState(false);
  const headerSet = useRef(false);

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData")) || {};
    } catch {
      return {};
    }
  }, []);

  const S3_URL = (process.env.REACT_APP_S3_PATH || "").replace(/\/*$/, "/");

  useLayoutEffect(() => {
    // Avoid infinite re-renders: updateHeader function identity changes each render
    if (!headerSet.current) {
      updateHeader?.({ backButtonRedirect: "/game" });
      headerSet.current = true;
    }
    const storedTheme = sessionStorage.getItem("themeData");
    if (storedTheme) setThemeData(JSON.parse(storedTheme));
  }, []);

  // Check gameover flag from backend (idempotent)
  useEffect(() => {
    if (!storedUserData?.sessionId) return;
    (async () => {
      try {
        const res = await alreadyPlayed({
          userId: storedUserData.userId || storedUserData.userid,
          sessionId: storedUserData.sessionId,
          organizationId: storedUserData.organizationId
        });
        if (res?.status === "success") setIsGameoverTrue(true);
      } catch {
        // ignore
      }
    })();
  }, [storedUserData]);

  // Block browser-back to Thankyou as well
  useEffect(() => {
    // Add a guard entry so browser-back won't return to Thankyou
    window.history.pushState(null, "", window.location.href);

    const onPop = () => {
      // Re-push the guard state, then redirect
      window.history.pushState(null, "", window.location.href);
      // If gameover is already true, keep user on leaderboard
      if (!isGameoverTrue) {
        navigate("/game", { replace: true });
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [navigate, isGameoverTrue]);

  // Ensure gameover=true is set (idempotent)
  useEffect(() => {
    if (!storedUserData?.sessionId) return;
    const timePlayed =
      sessionStorage.getItem("playedTime") || "00:00";
    updategameover({
      userId: storedUserData.userId || storedUserData.userid,
      sessionId: storedUserData.sessionId,
      organizationId: storedUserData.organizationId,
      time_played: timePlayed || "00:00"
    });
  }, [storedUserData]);

  useEffect(() => {
    if (!storedUserData?.sessionId) {
      setLoading(false);
      setError("Missing session. Please login again.");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await getLeaderboard({
        userId:         storedUserData.userId || storedUserData.userid,
        sessionId:      storedUserData.sessionId,
        organizationId: storedUserData.organizationId,
      });

      if (cancelled) return;

      if (res?.status === "success") {
        setRows(Array.isArray(res.data?.top) ? res.data.top : []);
        setMe(res.data?.me || null);
      } else {
        setError(res?.message || "Failed to load leaderboard");
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [storedUserData]);

  const bgUrl = themeData?.all_icon_image
    ? `${S3_URL}${themeData.all_icon_image}`
    : null;

  const dcUrl = themeData?.dreamcatchter_image
    ? `${S3_URL}${themeData.dreamcatchter_image}`
    : null;

  const btnColor = themeData?.button_color || "#E25569";
  const iconColor =
    themeData?.icon_color ||
    themeData?.landing_page_title_color ||
    "#ffffff";

  return (
    <div
      className="lb-stage"
      style={{
        ...(bgUrl ? { backgroundImage: `url(${bgUrl})` } : {}),
        "--lb-accent": iconColor
      }}
    >
      <div className="lb-overlay">
        <h1 className="lb-title">Leaderboard</h1>

        {/* Wrapper gives side dreamcatchers room to sit outside the card */}
        <div className="lb-card-wrap">
          {dcUrl && (
            <>
              {/* Desktop: left & right outside card */}
              <img className="lb-dc left"  src={dcUrl} alt="" draggable={false} />
              <img className="lb-dc right" src={dcUrl} alt="" draggable={false} />
            </>
          )}

          <div className="lb-card">
            {/* Mobile: single centered dreamcatcher inside card */}
            {dcUrl && (
              <img className="lb-dc center" src={dcUrl} alt="" draggable={false} />
            )}

            {loading ? (
              <div className="lb-state-msg">Loading…</div>
            ) : error ? (
              <div className="lb-state-msg">{error}</div>
            ) : (
              <table className="lb-table">
                <thead>
                  <tr>
                    <th className="lb-rank">Rank</th>
                    <th>Name</th>
                    <th className="lb-time">Time</th>
                    <th className="lb-points">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={`rank-${r.rank}-${r.userId}`}
                      className={r?.isMe ? "lb-me" : ""}
                    >
                      <td className="lb-rank">{String(r.rank).padStart(2, "0")}</td>
                      <td>{r.name}</td>
                      <td className="lb-time">{r.time}</td>
                      <td className="lb-points">{r.points}</td>
                    </tr>
                  ))}

                  {me && (
                    <tr key={`me-${me.rank}-${me.userId}`} className="lb-me">
                      <td className="lb-rank">{String(me.rank).padStart(2, "0")}</td>
                      <td>{me.name}</td>
                      <td className="lb-time">{me.time}</td>
                      <td className="lb-points">{me.points}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
